import {css, StyleDeclarationValue, StyleSheet} from 'aphrodite';
import React, {DragEventHandler} from 'react';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FlowchartBaseNode} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  ProcessFlowchartInteractiveNode,
  ProcessFlowchartNodeHeight,
  ProcessFlowchartNodeWidth,
  ProcessFlowchartStaticNode,
  ProcessFlowchartUIOnlyNodeType,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {getNodeDefinition} from 'processes/graph-builder-processes/ProcessFlowchartNodeTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE, BLUE_LIGHT, BLUE_POWDER_LIGHT, GRAY_DARK, WHITE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {ElementMouseEvent} from 'util/Types';

import {getNodeContents} from './util/getNodeContents';

export interface FlowchartBaseNodeRendererProps {
  data: ProcessFlowchartStaticNode | ProcessFlowchartInteractiveNode;
  children?: React.ReactNode;
  nodeStyles?: Array<StyleDeclarationValue>;
  showTooltip?: boolean;
  onClick?: (e: ElementMouseEvent) => void;
  isVisibleInSidebar?: boolean;
  onDragStart?: DragEventHandler;
  onDragEnd?: DragEventHandler;
  isStatic?: boolean;
}

// For now, not storing wrapper styles on the node instance data because these differ based on the node's width
// and whether the node is in the sidebar.
function getNodeWrapperClass(data: FlowchartBaseNode, isVisibleInSidebar?: boolean): object {
  let nodeWidthStyle = styles.wrapperDefault;

  switch (data.type) {
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
      nodeWidthStyle = styles.wrapperDiamond;
      break;
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      nodeWidthStyle = styles.wrapperPill;
      break;
    case ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE:
      nodeWidthStyle = styles.wrapperCircle;
      break;
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      if (isVisibleInSidebar) {
        nodeWidthStyle = styles.wrapperSidebar;
      }
      break;
    default:
      nodeWidthStyle = styles.wrapperDefault;
      break;
  }

  const width = data.instanceData.width;
  if (width >= ProcessFlowchartNodeWidth.WIDE) {
    nodeWidthStyle = styles.wrapperWide;
  }
  return nodeWidthStyle;
}

/**
 * Renders a base process flowchart node with basic styling for text and node
 */
export default function ProcessFlowchartBaseNodeRenderer({
  data,
  nodeStyles,
  onClick,
  children,
  isVisibleInSidebar,
  onDragStart,
  onDragEnd,
  isStatic,
}: FlowchartBaseNodeRendererProps): JSX.Element {
  const draggable = onDragStart != null;

  const nodeClass = css(
    styles.node,
    isVisibleInSidebar && styles.sidebarNode,
    data.isHighlighted && styles.highlightedNode,
    ...(nodeStyles ?? []),
    getNodeDefinition(data.type).nodeStyles.shapeStyle
  );

  const nodeNameTextClass = css(
    styles.nodeNameText,
    data.isHighlighted && !data.isInaccessible && styles.highlightedText,
    data.isInaccessible && styles.inaccessibleText,
    getNodeDefinition(data.type).nodeStyles.nameStyle
  );

  const nodeName = (name: string): JSX.Element => (
    <span
      className={nodeNameTextClass}
      {...getTestProps({component: 'ProcessFlowchartBaseNodeRenderer', element: 'text'})}
    >
      {name}
    </span>
  );

  const nodeContents = getNodeContents(
    data,
    nodeClass,
    draggable,
    children,
    onDragStart,
    onDragEnd,
    isStatic,
    nodeName
  );

  return (
    <Tooltip
      text={data.name}
      placement={FloatingPlacement.TOP}
      isDisabled={getNodeWrapperClass(data, isVisibleInSidebar) !== styles.wrapperSidebar}
    >
      <div
        className={css(getNodeWrapperClass(data, isVisibleInSidebar), onClick && styles.clickableNodeWrapper)}
        onClick={onClick}
        {...getTestProps({
          component: 'ProcessFlowchartBaseNodeRenderer',
          element: 'root',
          key: data.clientId,
        })}
      >
        {nodeContents}
      </div>
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  wrapperSidebar: {
    width: ProcessFlowchartNodeWidth.SIDEBAR,
    textAlign: 'center',
  },
  wrapperDefault: {
    width: ProcessFlowchartNodeWidth.DEFAULT,
    textAlign: 'center',
  },
  wrapperWide: {
    width: ProcessFlowchartNodeWidth.WIDE,
    textAlign: 'center',
  },
  wrapperSquare: {
    width: ProcessFlowchartNodeWidth.SQUARE,
    textAlign: 'center',
  },
  wrapperPill: {
    width: ProcessFlowchartNodeWidth.PILL,
    height: ProcessFlowchartNodeHeight.PILL,
    textAlign: 'center',
  },
  wrapperCircle: {
    textAlign: 'center',
  },
  wrapperDiamond: {
    width: ProcessFlowchartNodeWidth.DIAMOND,
    textAlign: 'center',
  },
  clickableNodeWrapper: {
    pointerEvents: 'all',
  },
  node: {
    border: `1px solid ${GRAY_DARK}`,
    borderRadius: '3px',
    background: WHITE,
    padding: '6px 12px',
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
    lineHeight: '16px',
  },
  nodeNameText: {
    fontWeight: 'normal',
    userSelect: 'none',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'block',
    width: '100%',
  },
  sidebarNode: {
    height: '30px',
  },
  highlightedNode: {
    borderColor: BLUE_LIGHT,
    backgroundColor: BLUE_POWDER_LIGHT,
  },
  highlightedText: {
    color: BLUE,
  },
  inaccessibleText: {
    color: GRAY_DARK,
  },
});
